/**
 * The Flow route guards for "Flow is not switched on for you", and for the people invited past that.
 *
 *   flowAccessGuard             /flow (canActivate AND canActivateChild): Flow on for the person lets them through, as
 *                               before. Flow off sends them to the Flow-off page, unless the address is ONE session's
 *                               own screen and the API says they are invited to it. Child checks matter: the parent's
 *                               canActivate does not run again when the person moves from one child to another.
 *   flowOrSessionGuard(flag)    the three session screens (`m/:flowPlanId`, `live/:sessionId`, `boards/:boardId`): the
 *                               flag as before; otherwise the session grant the parent just took.
 *   flowOffPageGuard            the Flow-off page itself: a person who has Flow is sent to Flow.
 *   flowBoardCodeGuard          `/m/:code/board`: follows the entitlement. A live board code is an invitation. With Flow on
 *                               it asks nothing: the board's own page decides, as before.
 *
 * Nothing here changes what a person with Flow sees. The API refuses whatever an invited person may not do.
 */
import { Injector, inject } from '@angular/core';
import { Router, type CanActivateChildFn, type CanActivateFn, type UrlTree } from '@angular/router';
import { isAdminPreview } from '../../../core/guards/admin-preview';
import { isErr } from '../../../core/models/api-result';
import { FeatureFlagsViewerService, type FeatureFlagKey } from '../../../core/services/feature-flags-viewer.service';
import { GraphQLClientService } from '../../../core/services/graphql-client.service';
import { ViewerService } from '../../../core/services/viewer.service';
import { HOME_ROUTE } from '../../../core/utils/home-link';
import { RESOLVE_FLOW_JOIN_CODE_QUERY, type FlowJoinCodeDto } from '../../calls/data/meeting-call.operations';
import { FlowAccessService } from './flow-access.service';
import { FLOW_OFF_ROUTE } from './flow-off-route';
import { flowSessionTarget } from './flow-session-target';

export const flowAccessGuard: CanActivateFn & CanActivateChildFn = async (route, state) => {
  if (isAdminPreview(route)) return true;
  const viewer = inject(ViewerService);
  const flags = inject(FeatureFlagsViewerService);
  const router = inject(Router);
  const access = inject(FlowAccessService);

  await viewer.ensureLoaded();
  const allowed = flags.isOwn('flow');
  flags.recordGate('flow', allowed);
  if (allowed) {
    // Flow is on for them (again): a "switched off" notice from before is over.
    access.clearSwitchedOff();
    return true;
  }
  if (await access.admit(state?.url)) return true;
  return flowOffTree(router, access);
};

/** The Flow-off page; the "switched off" wording when a live connection has just told the person so. */
function flowOffTree(router: Router, access: FlowAccessService): UrlTree {
  return router.createUrlTree([FLOW_OFF_ROUTE], access.switchedOff() ? { queryParams: { why: 'switched-off' } } : {});
}

export function flowOrSessionGuard(flagKey: FeatureFlagKey): CanActivateFn {
  return async (route, state) => {
    if (isAdminPreview(route)) return true;
    const viewer = inject(ViewerService);
    const flags = inject(FeatureFlagsViewerService);
    const router = inject(Router);
    const injector = inject(Injector);

    await viewer.ensureLoaded();
    // `is` includes the session grant the parent guard took for an invited person; the parent has already refused
    // anyone who is neither on Flow nor invited, so this only decides between Flow's own parts.
    const allowed = flags.is(flagKey);
    flags.recordGate(flagKey, allowed);
    if (allowed) return true;
    if (flowSessionTarget(state?.url) && (await injector.get(FlowAccessService).admit(state?.url))) return true;
    // Flow is on but this part is not: Home, as it always was. Flow off: the explanation.
    if (flags.is('flow')) return router.createUrlTree([HOME_ROUTE]);
    return flowOffTree(router, injector.get(FlowAccessService));
  };
}

export const flowOffPageGuard: CanActivateFn = async (route) => {
  if (isAdminPreview(route)) return true;
  const viewer = inject(ViewerService);
  const flags = inject(FeatureFlagsViewerService);
  const router = inject(Router);
  await viewer.ensureLoaded();
  return flags.isOwn('flow') ? router.createUrlTree(['/flow']) : true;
};

export const flowBoardCodeGuard: CanActivateFn = async (route) => {
  if (isAdminPreview(route)) return true;
  const viewer = inject(ViewerService);
  const flags = inject(FeatureFlagsViewerService);
  const router = inject(Router);
  const graphql = inject(GraphQLClientService);
  const code = route.paramMap.get('code') ?? '';

  await viewer.ensureLoaded();
  const flagOn = flags.is('flow.instant');
  flags.recordGate('flow.instant', flagOn);
  // Flow on: nothing to ask, the board's own page decides, as it always did. (A session link that reaches the board
  // route after sign-in comes in through `/m/<code>/in`, which asks the API what the code is once signed in.)
  if (flagOn) return true;
  // Flow off for this person: a live board code is an invitation, and the API makes the final call on joining.
  const read = await graphql.queryResult<FlowJoinCodeDto>(RESOLVE_FLOW_JOIN_CODE_QUERY, { code }, 'no-cache');
  const kind = isErr(read) ? null : (read.data?.kind ?? null);
  if (kind === 'SESSION' || kind === 'MEETING') return router.createUrlTree(['/m', code]);
  return kind === 'BOARD' ? true : router.createUrlTree([FLOW_OFF_ROUTE]);
};
